import { Box, Checkbox, Typography } from "@mui/material";
import classNames from "classnames";

import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoMenuItem } from "@/shared/components/RobotoMenuItem";
import { LayoutRecord } from "@/shared/domain/layouts";
import { MoreVertIcon, StarIcon } from "@/shared/foundation/icons/mui";

import styles from "./LayoutMenuItem.module.css";

interface LayoutMenuItemProps {
  layout: LayoutRecord;
  onSelect: (layout: LayoutRecord) => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  isActive?: boolean;
  isDefault?: boolean;
  /** Indent + draw a tree guide line, marking the layout as sitting under a folder. */
  isNested?: boolean;
  /** Show a checkbox; clicking the row toggles selection instead of loading the layout. */
  selectMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (layout: LayoutRecord) => void;
}

export function LayoutMenuItem({
  layout,
  onSelect,
  onOpenContextMenu,
  isActive = false,
  isDefault = false,
  isNested = false,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
}: LayoutMenuItemProps) {
  return (
    <RobotoMenuItem
      // In select mode a row click toggles selection rather than loading the layout, so it
      // must not count toward the SelectLayoutClicked (layout loaded) metric.
      eventName={selectMode ? "LayoutSelectionToggled" : "SelectLayoutClicked"}
      eventProperties={{
        layoutId: layout.layout_id,
        layoutName: layout.name,
      }}
      disableRipple
      onClick={() => (selectMode ? onToggleSelect?.(layout) : onSelect(layout))}
      className={classNames(styles.layoutItem, {
        [styles.nested]: isNested,
      })}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        {selectMode && (
          <Checkbox
            checked={isSelected}
            readOnly
            size="small"
            tabIndex={-1}
            disableRipple
            className={styles.selectCheckbox}
            sx={{ p: 0, mr: 1 }}
          />
        )}
        <Typography variant="body2" fontWeight={isActive ? 700 : 400} noWrap>
          {layout.name}
        </Typography>
        {isDefault && (
          <StarIcon fontSize="small" sx={{ color: "text.primary", ml: 1 }} />
        )}
      </Box>
      {!selectMode && (
        <RobotoIconButton
          eventName={"LayoutContextMenuOpened"}
          eventProperties={{
            layoutId: layout.layout_id,
            layoutName: layout.name,
          }}
          size="small"
          onClick={(e) => onOpenContextMenu(e, layout)}
        >
          <MoreVertIcon className={styles.rightIcon} />
        </RobotoIconButton>
      )}
    </RobotoMenuItem>
  );
}
